import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SyncIcon from "@material-ui/icons/Sync";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
  tableWrapper: {
    overflow: "auto",
  },
  syncing: {
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
}));

const statusColor = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "APPROVED":
      return "primary";
    case "REJECTED":
      return "error";
    case "PAUSED":
      return "warning";
    case "PENDING":
    default:
      return "default";
  }
};

const componentsSummary = (components = []) => {
  const counts = {};
  components.forEach((c) => {
    const t = String(c.type || "").toUpperCase();
    counts[t] = (counts[t] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([k, v]) => (v > 1 ? `${k}×${v}` : k))
    .join(", ");
};

const WhatsAppTemplates = () => {
  const classes = useStyles();
  const history = useHistory();

  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("APPROVED");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [languageFilter, setLanguageFilter] = useState("ALL");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const loadWhatsapps = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/");
      const list = Array.isArray(data) ? data : [];
      const oficial = list.filter((w) => w.channel === "whatsapp_oficial");
      setWhatsapps(oficial);
      if (oficial.length === 1) setWhatsappId(String(oficial[0].id));
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    if (!whatsappId) {
      setTemplates([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/whatsapp/${whatsappId}/message-templates`
      );
      const rows = data?.data?.data || data?.data || [];
      setTemplates(Array.isArray(rows) ? rows : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [whatsappId]);

  useEffect(() => {
    loadWhatsapps();
  }, [loadWhatsapps]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSync = async () => {
    if (!whatsappId) {
      toast.error("Selecione uma conexão.");
      return;
    }
    setSyncing(true);
    try {
      await api.get(`/whatsapp/sync-templates/${whatsappId}`);
      toast.success("Templates sincronizados com a Meta.");
      await loadTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const distinctValues = useMemo(() => {
    const statuses = new Set();
    const categories = new Set();
    const languages = new Set();
    templates.forEach((t) => {
      if (t.status) statuses.add(String(t.status).toUpperCase());
      if (t.category) categories.add(String(t.category).toUpperCase());
      if (t.language) languages.add(String(t.language));
    });
    return {
      statuses: Array.from(statuses).sort(),
      categories: Array.from(categories).sort(),
      languages: Array.from(languages).sort(),
    };
  }, [templates]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return templates.filter((t) => {
      if (statusFilter !== "ALL" && String(t.status || "").toUpperCase() !== statusFilter) return false;
      if (categoryFilter !== "ALL" && String(t.category || "").toUpperCase() !== categoryFilter) return false;
      if (languageFilter !== "ALL" && String(t.language || "") !== languageFilter) return false;
      if (q) {
        const hay = [t.name, t.language, t.category, t.status]
          .map((v) => String(v || "").toLowerCase())
          .join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [templates, filter, statusFilter, categoryFilter, languageFilter]);

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/whatsapp/${whatsappId}/message-templates`, {
        params: { name: deleting.name },
      });
      toast.success("Template removido na Meta.");
      setConfirmOpen(false);
      setDeleting(null);
      loadTemplates();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `Remover template "${deleting.name}"?` : ""}
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleting(null);
        }}
        onConfirm={handleConfirmDelete}
      >
        A remoção na Meta é <strong>irreversível</strong> e apaga todas as
        traduções (idiomas) associadas a este nome.
      </ConfirmationModal>

      <MainHeader>
        <Title>Templates WhatsApp (Meta)</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            startIcon={
              <SyncIcon className={syncing ? classes.syncing : ""} />
            }
            onClick={handleSync}
            disabled={!whatsappId || syncing}
          >
            {syncing ? "Sincronizando…" : "Sincronizar com Meta"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push("/whatsapp-template-create")}
          >
            Criar novo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Conexão API Oficial"
              value={whatsappId}
              onChange={(e) => setWhatsappId(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            >
              {whatsapps.map((w) => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.name} (#{w.id})
                </MenuItem>
              ))}
            </TextField>
            {whatsapps.length === 0 && (
              <Typography className={classes.hint}>
                Nenhuma conexão whatsapp_oficial encontrada.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Buscar por nome, idioma, categoria ou status"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="ALL">Todos</MenuItem>
              {distinctValues.statuses.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Categoria"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="ALL">Todas</MenuItem>
              {distinctValues.categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Idioma"
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="ALL">Todos</MenuItem>
              {distinctValues.languages.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.tableWrapper}>
        <Box px={2} pt={1.5}>
          <Typography variant="caption" color="textSecondary">
            {filtered.length} template{filtered.length !== 1 ? "s" : ""} exibido{filtered.length !== 1 ? "s" : ""}
            {filtered.length !== templates.length && ` (de ${templates.length})`}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Idioma</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Componentes</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum template encontrado.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((t) => {
                const editable = ["APPROVED", "REJECTED", "PAUSED"].includes(
                  String(t.status || "").toUpperCase()
                );
                return (
                  <TableRow key={`${t.id || t.name}-${t.language}`}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.language}</TableCell>
                    <TableCell>{t.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.status}
                        color={statusColor(t.status)}
                        title={
                          String(t.status || "").toUpperCase() === "REJECTED"
                            ? "Rejeitado pela Meta — não pode ser enviado. Corrija no Business Manager e sincronize."
                            : String(t.status || "").toUpperCase() === "PAUSED"
                            ? "Pausado pela Meta — não pode ser enviado até reativar."
                            : undefined
                        }
                      />
                    </TableCell>
                    <TableCell>{componentsSummary(t.components)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        disabled={!editable || !t.id}
                        title={
                          editable
                            ? "Editar"
                            : "Só é possível editar templates APPROVED/REJECTED/PAUSED"
                        }
                        onClick={() =>
                          history.push(
                            `/whatsapp-templates/${whatsappId}/${t.id}/edit?name=${encodeURIComponent(
                              t.name
                            )}&language=${encodeURIComponent(t.language)}`
                          )
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleting(t);
                          setConfirmOpen(true);
                        }}
                        title="Remover"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default WhatsAppTemplates;
